import { useEffect, useRef, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { stripSystemTags } from "@/lib/messageMask";

/**
 * Combine unread notifications + unread messages and:
 *  1. Update the W3C App Badging API (icon badge on installed PWA / Android /
 *     macOS dock / Windows). iOS Safari ne supporte pas encore (badge natif
 *     nécessiterait Capacitor + APNs).
 *  2. Tente d'afficher une notification système (Notification API) à chaque
 *     nouveau message ou notification reçue en realtime, pour informer
 *     l'utilisateur en dehors de l'app.
 *  3. Met à jour le titre du document "(N) Click&Gard" pour la visibilité
 *     dans les onglets desktop.
 *
 * Le clic ouvre désormais la section correspondant réellement à l'événement.
 */
export const useAppBadge = () => {
  const { user } = useAuth();
  const [unreadTotal, setUnreadTotal] = useState(0);
  const permissionAsked = useRef(false);
  const lastBadgeCount = useRef(0);

  const ensureNotificationPermission = useCallback(async () => {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (permissionAsked.current) return;
    permissionAsked.current = true;
    if (Notification.permission === "default") {
      try {
        await Notification.requestPermission();
      } catch {
        /* user dismissed */
      }
    }
  }, []);

  const showSystemNotification = useCallback((
    title: string,
    body: string,
    route = "/notifications",
    tag = "clickandgard-activity"
  ) => {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission !== "granted") return;
    if (document.visibilityState === "visible") return;

    try {
      const n = new Notification(title, {
        body,
        icon: "/favicon.png",
        badge: "/favicon.png",
        tag,
        ...({ renotify: true } as Record<string, unknown>),
      } as NotificationOptions);
      n.onclick = () => {
        window.focus();
        window.location.href = route;
        n.close();
      };
    } catch {
      /* unsupported */
    }
  }, []);

  const resolveNotificationRoute = useCallback((notification: {
    type?: string;
    metadata?: unknown;
  }) => {
    const type = notification.type ?? "";
    const meta = (notification.metadata ?? {}) as Record<string, unknown>;
    const bookingId = typeof meta.booking_id === "string" ? meta.booking_id : null;
    const conversationId = typeof meta.conversation_id === "string" ? meta.conversation_id : null;
    const requestId = typeof meta.request_id === "string" ? meta.request_id : null;

    if (type.startsWith("care_request")) {
      return requestId ? `/demandes?request=${encodeURIComponent(requestId)}` : "/demandes";
    }
    if (type === "new_message" || type === "admin_message") {
      return conversationId ? `/messages?conversation=${encodeURIComponent(conversationId)}` : "/messages";
    }
    if (type.startsWith("booking_")) {
      return bookingId ? `/mes-reservations?booking=${encodeURIComponent(bookingId)}` : "/mes-reservations";
    }
    if (["payout_requested", "payout_approved", "payout_paid", "payout_rejected", "payment_released"].includes(type)) {
      return "/mes-gains";
    }
    if (type === "account_suspended" || type === "account_reactivated") return "/settings";
    return "/notifications";
  }, []);

  const updateBadge = useCallback((count: number) => {
    setUnreadTotal(count);
    try {
      const nav = navigator as Navigator & {
        setAppBadge?: (n?: number) => Promise<void>;
        clearAppBadge?: () => Promise<void>;
      };
      if (count > 0 && typeof nav.setAppBadge === "function") {
        nav.setAppBadge(count).catch(() => {});
      } else if (typeof nav.clearAppBadge === "function") {
        nav.clearAppBadge().catch(() => {});
      }
    } catch {
      /* unsupported */
    }

    const baseTitle = document.title.replace(/^\(\d+\+?\)\s*/, "");
    document.title = count > 0 ? `(${count > 99 ? "99+" : count}) ${baseTitle}` : baseTitle;
    lastBadgeCount.current = count;
  }, []);

  const refresh = useCallback(async () => {
    if (!user) {
      updateBadge(0);
      return;
    }

    const [{ count: notifCount }, { data: convos }] = await Promise.all([
      supabase
        .from("notifications")
        .select("id", { count: "exact", head: true })
        .eq("user_id", user.id)
        .eq("read", false),
      supabase
        .from("conversations")
        .select("id")
        .or(`parent_id.eq.${user.id},pro_id.eq.${user.id}`),
    ]);

    let msgCount = 0;
    if (convos && convos.length > 0) {
      const ids = convos.map((c) => c.id);
      const { count } = await supabase
        .from("messages")
        .select("id", { count: "exact", head: true })
        .in("conversation_id", ids)
        .neq("sender_id", user.id)
        .eq("read", false);
      msgCount = count ?? 0;
    }

    updateBadge((notifCount ?? 0) + msgCount);
  }, [user, updateBadge]);

  useEffect(() => {
    if (!user) {
      updateBadge(0);
      return;
    }

    ensureNotificationPermission();
    refresh();

    const channel = supabase
      .channel(`app-badge-${user.id}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` },
        (payload) => {
          const n = payload.new as { title?: string; body?: string; type?: string; metadata?: unknown };
          const route = resolveNotificationRoute(n);
          const isCareAnnouncement = n.type === "care_request_new";
          showSystemNotification(
            isCareAnnouncement ? "📢 Nouvelle annonce de garde" : (n.title ?? "Click&Gard"),
            isCareAnnouncement
              ? "Un parent a publié une annonce de garde. Ceci n’est pas un message privé."
              : (n.body ?? "Vous avez une nouvelle notification"),
            route,
            isCareAnnouncement ? "clickandgard-care-request" : `clickandgard-${n.type ?? "activity"}`
          );
          refresh();
        }
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` },
        () => refresh()
      )
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "messages" },
        (payload) => {
          const m = payload.new as { sender_id?: string; content?: string; conversation_id?: string };
          if (m.sender_id && m.sender_id !== user.id) {
            const preview = stripSystemTags(m.content ?? "");
            if (preview) {
              const route = m.conversation_id
                ? `/messages?conversation=${encodeURIComponent(m.conversation_id)}`
                : "/messages";
              showSystemNotification(
                "💬 Nouveau message privé Click&Gard",
                preview.slice(0, 120),
                route,
                `clickandgard-message-${m.conversation_id ?? "new"}`
              );
            }
          }
          refresh();
        }
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "messages" },
        () => refresh()
      )
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "admin_direct_messages", filter: `recipient_id=eq.${user.id}` },
        (payload) => {
          const m = payload.new as { content?: string };
          showSystemNotification(
            "Message de l'équipe Click&Gard",
            stripSystemTags(m.content ?? "").slice(0, 120) || "Nouveau message administrateur",
            "/notifications",
            "clickandgard-admin-message"
          );
          refresh();
        }
      )
      .subscribe();

    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);

    return () => {
      supabase.removeChannel(channel);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [user, refresh, updateBadge, ensureNotificationPermission, showSystemNotification, resolveNotificationRoute]);

  return { unreadTotal, refresh };
};
